import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle, Monitor, Shield } from "lucide-react";
import { MonitoringConfig, DialogStep, MonitorDetectionAction } from "@/types/video-call";

interface MonitoringConfigDialogProps {
  open: boolean;
  config: MonitoringConfig;
  dialogStep: DialogStep;
  onConfigChange: (key: keyof MonitoringConfig, value: any) => void;
  onStartCall: () => void;
  onBackStep: () => void;
  onClose: () => void;
}

export const MonitoringConfigDialog: React.FC<MonitoringConfigDialogProps> = ({
  open,
  config,
  dialogStep,
  onConfigChange,
  onStartCall,
  onBackStep,
  onClose,
}) => {
  if (dialogStep !== "monitoring") return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Shield className="w-6 h-6 mr-2 text-foreground" />
            Configure Monitoring Options
          </DialogTitle>
          <DialogDescription>
            Set up proctoring and security monitoring for this video call.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Core Monitoring Options */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground dark:text-white">
              Core Monitoring
            </h4>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Tab Change Detection</Label>
                  <p className="text-xs text-muted-foreground dark:text-ink-faint">
                    Monitor when user switches tabs or windows
                  </p>
                </div>
                <Switch
                  checked={config.tabChangeDetection}
                  onCheckedChange={(checked) => onConfigChange("tabChangeDetection", checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">External Monitor Detection</Label>
                  <p className="text-xs text-muted-foreground dark:text-ink-faint">
                    Detect and prevent multiple monitor usage
                  </p>
                </div>
                <Switch
                  checked={config.externalMonitorDetection}
                  onCheckedChange={(checked) => onConfigChange("externalMonitorDetection", checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Screen Sharing Required</Label>
                  <p className="text-xs text-muted-foreground dark:text-ink-faint">
                    Require full screen sharing for proctoring
                  </p>
                </div>
                <Switch
                  checked={config.screenSharingEnabled}
                  onCheckedChange={(checked) => onConfigChange("screenSharingEnabled", checked)}
                />
              </div>
            </div>
          </div>

          {/* Face Detection Options */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground dark:text-white">
              Face Detection
            </h4>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Blur Detection</Label>
                  <p className="text-xs text-muted-foreground dark:text-ink-faint">
                    Monitor face focus and clarity
                  </p>
                </div>
                <Switch
                  checked={config.blurDetection}
                  onCheckedChange={(checked) => onConfigChange("blurDetection", checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Viewport Detection</Label>
                  <p className="text-xs text-muted-foreground dark:text-ink-faint">
                    Monitor face position within camera frame
                  </p>
                </div>
                <Switch
                  checked={config.viewportDetection}
                  onCheckedChange={(checked) => onConfigChange("viewportDetection", checked)}
                />
              </div>
            </div>
          </div>

          {/* Monitor Detection Action */}
          {config.externalMonitorDetection && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Multiple Monitor Detection Action
              </Label>
              <Select
                value={config.monitorDetectionAction}
                onValueChange={(value: MonitorDetectionAction) => 
                  onConfigChange("monitorDetectionAction", value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warning">
                    Warning - Show alert but continue
                  </SelectItem>
                  <SelectItem value="pause">
                    Pause - Pause session until resolved
                  </SelectItem>
                  <SelectItem value="block">
                    Block - Prevent session from starting
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Strict Mode */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">Strict Monitoring Mode</Label>
                <p className="text-xs text-muted-foreground dark:text-ink-faint">
                  Enhanced sensitivity for all monitoring features
                </p>
              </div>
              <Switch
                checked={config.strictMonitorMode}
                onCheckedChange={(checked) => onConfigChange("strictMonitorMode", checked)}
              />
            </div>
          </div>

          {/* Warning Notice */}
          {(config.externalMonitorDetection || config.screenSharingEnabled) && (
            <div className="bg-mid-bg-mid-bg/20 border border-mid-line rounded-lg p-3">
              <div className="flex items-start">
                <AlertTriangle className="w-4 h-4 text-mid mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-mid">
                    Security Notice
                  </p>
                  <p className="text-xs text-mid mt-1">
                    {config.externalMonitorDetection && "Multiple monitor detection is enabled. "}
                    {config.screenSharingEnabled && "Screen sharing will be required. "}
                    Ensure compliance with these requirements before starting the call.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onBackStep}>
            Back
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onStartCall}>
            Start Call
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};